import { useTranslation } from '@/contexts/i18n-context';
import { Button } from '@/components/button';
import { Form } from '@/components/form';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextInput } from '@/components/inputs/text-input';
import styles from './styles.module.css';
import { requiredString } from '@/util/validators';
import { Expander } from '@/components/expander';
import { IllnessIcon } from '../icons/illness-icon';
import { CreateIllnessParams, IllnessType } from '@/api/api.schemas';
import { Select } from '@/components/inputs/select';
import { MedicalHistoryItemList } from '../medical-history-item-list';
import { InputLabel } from '@/components/inputs/input-label';
import { useMediaQuery } from '@/hooks/use-media-query';
import clsx from 'clsx';
import { Illness } from '@/hooks/use-medical-history-state';

const illnessSchema = z.object({
  text: requiredString(),
  type: z.nativeEnum(IllnessType).optional(),
});

type FormValues = z.infer<typeof illnessSchema>;

interface IllnessesFormProps {
  onRemove: (id: string) => void;
  onAdd: (illness: CreateIllnessParams) => void;
  illnesses: Illness[];
  isOpen?: boolean;
  onExpanderClick?: () => void;
}

const translationBase = 'visit.medicalHistory.illnessesForm';

export function IllnessesForm({
  onRemove,
  onAdd,
  illnesses,
  isOpen,
  onExpanderClick,
}: IllnessesFormProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('--screen-md');

  const form = useForm<FormValues>({
    resolver: zodResolver(illnessSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    defaultValues: {
      text: '',
    },
  });

  const onSubmit = (data: FormValues) => {
    onAdd(data);
    form.reset();
  };

  return (
    <Expander
      icon={<IllnessIcon className={styles.icon} />}
      title={t(`${translationBase}.title`)}
      indicatorText={t(`${translationBase}.indicatorText`)}
      collapsedText={illnesses.map((illness) => illness.text).join(', ')}
      isOpen={isOpen}
      onClick={onExpanderClick}
    >
      <Form onSubmit={onSubmit} form={form} className={styles.form}>
        <div className={styles.formContainer}>
          {!isMobile && (
            <>
              <InputLabel
                className={styles.singleColumn}
                label={t(`${translationBase}.illnessLabel`)}
                htmlFor="text-input"
              />
              <InputLabel
                className={styles.singleColumn}
                label={t(`${translationBase}.illnessTypeLabel`)}
                htmlFor="type-select"
              />
            </>
          )}
          <TextInput
            className={clsx(styles.input, styles.singleColumn)}
            label={isMobile ? t(`${translationBase}.illnessLabel`) : undefined}
            id="text-input"
            data-testid="medical-history-illness-input"
            name="text"
            placeholder={t(`${translationBase}.illnessPlaceholder`)}
            withIconsIndicators={false}
            autoComplete="off"
          />
          <Select
            className={clsx(styles.input, styles.singleColumn)}
            label={isMobile ? t(`${translationBase}.illnessTypeLabel`) : undefined}
            placeholder={t(`${translationBase}.illnessTypePlaceholder`)}
            id="type-select"
            name="type"
            options={Object.values(IllnessType).map((type) => ({
              label: t(`${translationBase}.illnessType.${type}`),
              value: type,
            }))}
          />
          <Button
            type="submit"
            className={styles.addButton}
            data-testid="medical-history-illnesses-add"
          >
            {t(`${translationBase}.addButton`)}
          </Button>
        </div>
        <MedicalHistoryItemList items={illnesses} displayType onRemove={onRemove} />
      </Form>
    </Expander>
  );
}
